import api from './api';
import { AxiosResponse } from 'axios';

/**
 * Serviço de polling para buscar atualizações periódicas de tarefas, avisos e atividades
 * Substitui a funcionalidade SSE que estava apresentando problemas de CORS
 */
class PollingService {
  private pollingIntervals: Record<string, NodeJS.Timeout> = {};
  private lastFetchTimes: Record<string, number> = {
    tarefas: 0,
    avisos: 0,
    atividades: 0,
    estatisticas: 0,
    usuario: 0
  };
  
  // Controle de debounce para evitar chamadas excessivas
  private debounceTimers: Record<string, NodeJS.Timeout | null> = {
    tarefas: null,
    avisos: null,
    atividades: null,
    estatisticas: null,
    usuario: null
  };
  
  // Tempo mínimo entre chamadas (em ms)
  private minTimeBetweenCalls: number = 300000; // 5 minutos para evitar chamadas excessivas
  
  // Flag para controlar se o polling está em execução
  private isPollingRunning: Record<string, boolean> = {
    tarefas: false,
    avisos: false,
    atividades: false,
    estatisticas: false,
    usuario: false
  };

  /**
   * Inicia o polling para um tipo específico de dados
   * @param tipo 'tarefas', 'avisos' ou 'atividades'
   * @param callback Função a ser chamada quando houver novos dados
   * @param intervalo Intervalo em milissegundos (padrão: 3 minutos)
   */
  iniciarPolling(
    tipo: 'tarefas' | 'avisos' | 'atividades' | 'estatisticas' | 'usuario',
    callback: (dados: any[]) => void,
    intervalo: number = 300000 // Padronizado para 5 minutos (300000ms)
  ): void {
    // Verificar se já existe um polling ativo para este tipo
    if (this.isPollingRunning[tipo]) {
      return;
    }
    
    // Parar polling anterior se existir (garantia adicional)
    this.pararPolling(tipo);
    
    // Marcar como ativo
    this.isPollingRunning[tipo] = true;

    // Buscar dados imediatamente na primeira vez após um pequeno delay
    // para evitar múltiplas chamadas simultâneas
    setTimeout(() => {
      this.buscarDados(tipo, callback);
    }, 500);

    // Usar o maior valor entre o intervalo fornecido e o tempo mínimo
    const intervaloSeguro = Math.max(intervalo, this.minTimeBetweenCalls);

    // Configurar intervalo para buscar periodicamente
    this.pollingIntervals[tipo] = setInterval(() => {
      this.buscarDados(tipo, callback);
    }, intervaloSeguro);


  }
  
  /**
   * Inicia o polling usando uma função personalizada
   * @param tipo 'tarefas', 'avisos' ou 'atividades'
   * @param funcao Função personalizada a ser executada periodicamente
   * @param intervalo Intervalo em milissegundos
   */
  iniciarPollingFuncao(
    tipo: 'tarefas' | 'avisos' | 'atividades' | 'estatisticas' | 'usuario',
    funcao: () => Promise<void> | void,
    intervalo: number = 300000 // Padronizado para 5 minutos (300000ms) para reduzir chamadas à API
  ): void {
    // Parar polling anterior se existir
    this.pararPolling(tipo);
    
    // Wrapper para a função que implementa controle de frequência
    const funcaoControlada = async () => {
      // Verificar se já existe um debounce timer para este tipo
      if (this.debounceTimers[tipo]) {

        return;
      }
      
      // Verificar se passou tempo suficiente desde a última chamada
      const agora = Date.now();
      const tempoDesdeUltimaChamada = agora - this.lastFetchTimes[tipo];
      
      if (tempoDesdeUltimaChamada < this.minTimeBetweenCalls) {

        
        // Configurar um debounce para executar após o tempo mínimo
        this.debounceTimers[tipo] = setTimeout(() => {
          this.debounceTimers[tipo] = null;
          funcao();
        }, this.minTimeBetweenCalls - tempoDesdeUltimaChamada);
        
        return;
      }
      
      // Atualizar o timestamp antes da chamada
      this.lastFetchTimes[tipo] = agora;
      
      // Executar a função original
      await funcao();
    };

    // Executar a função controlada imediatamente na primeira vez
    funcaoControlada();

    // Configurar intervalo para executar periodicamente
    this.pollingIntervals[tipo] = setInterval(funcaoControlada, intervalo);


  }

  /**
   * Para o polling para um tipo específico de dados
   * @param tipo 'tarefas', 'avisos' ou 'atividades'
   */
  pararPolling(tipo: 'tarefas' | 'avisos' | 'atividades' | 'estatisticas' | 'usuario'): void {
    // Parar o intervalo se existir
    if (this.pollingIntervals[tipo]) {
      clearInterval(this.pollingIntervals[tipo]);
      delete this.pollingIntervals[tipo];
    }
    
    // Limpar o debounce timer se existir
    if (this.debounceTimers[tipo]) {
      clearTimeout(this.debounceTimers[tipo]!);
      this.debounceTimers[tipo] = null;
    }
    
    // Resetar a flag de controle
    this.isPollingRunning[tipo] = false;
    

  }

  /**
   * Busca dados do servidor para atualizações
   * @param tipo 'tarefas', 'avisos' ou 'atividades'
   * @param callback Função a ser chamada quando houver novos dados
   */
  private async buscarDados(
    tipo: 'tarefas' | 'avisos' | 'atividades' | 'estatisticas' | 'usuario',
    callback: (dados: any[]) => void
  ): Promise<void> {
    // Verificar se o polling está ativo para este tipo
    if (!this.isPollingRunning[tipo]) {

      return;
    }
    
    // Verificar se já existe um debounce timer para este tipo
    if (this.debounceTimers[tipo]) {

      return;
    }
    
    // Verificar se passou tempo suficiente desde a última chamada
    const agora = Date.now();
    const tempoDesdeUltimaChamada = agora - this.lastFetchTimes[tipo];
    
    if (tempoDesdeUltimaChamada < this.minTimeBetweenCalls) {

      
      // Não agendar nova chamada, o intervalo regular já vai cuidar disso
      // Isso evita acumular múltiplas chamadas agendadas
      return;
    }
    
    try {
      // Atualizar o timestamp antes da chamada
      this.lastFetchTimes[tipo] = agora;
      
      let response: AxiosResponse<any>;
      
      // Usar as mesmas chamadas da carga inicial, sem filtros de data
      // Isso garante consistência e evita problemas com os filtros

      
      // Chamada específica para cada tipo de dados
      if (tipo === 'estatisticas') {
        response = await api.get('/estatisticas');
      } else if (tipo === 'usuario') {
        response = await api.get('/auth/me');
      } else {
        // Chamada padrão para tarefas, avisos e atividades
        response = await api.get(`/${tipo}`);
      }
      
      // Atualizar timestamp da última busca
      this.lastFetchTimes[tipo] = Date.now();
      
      // Se houver dados, chamar o callback
      if (response?.data) {
        // Para avisos, os dados estão dentro da propriedade 'avisos'
        if (tipo === 'avisos' && response.data.avisos && Array.isArray(response.data.avisos)) {
          if (response.data.avisos.length > 0) {

            callback(response.data.avisos);
          }
        } 
        // Para estatísticas, passar o objeto completo
        else if (tipo === 'estatisticas') {

          callback([response.data]); // Envolver em array para manter compatibilidade com a interface
        }
        // Para dados do usuário
        else if (tipo === 'usuario') {

          callback([response.data.usuario]); // Envolver em array para manter compatibilidade com a interface
        }
        // Para tarefas e atividades, a resposta já é um array
        else if (Array.isArray(response.data)) {
          if (response.data.length > 0) {

            callback(response.data);
          }
        }
      }
    } catch (error) {
      console.error(`Erro ao buscar ${tipo}:`, error);
    }
  }

  /**
   * Para todos os pollings ativos
   */
  pararTodos(): void {
    // Parar todos os intervalos
    Object.keys(this.pollingIntervals).forEach(tipo => {
      const tipoKey = tipo as 'tarefas' | 'avisos' | 'atividades' | 'estatisticas' | 'usuario';
      clearInterval(this.pollingIntervals[tipoKey]);
      delete this.pollingIntervals[tipoKey];
    });
    
    // Limpar todos os debounce timers
    Object.keys(this.debounceTimers).forEach(tipo => {
      const tipoKey = tipo as 'tarefas' | 'avisos' | 'atividades' | 'estatisticas' | 'usuario';
      if (this.debounceTimers[tipoKey]) {
        clearTimeout(this.debounceTimers[tipoKey]!);
        this.debounceTimers[tipoKey] = null;
      }
    });
    
    // Resetar todas as flags de controle
    Object.keys(this.isPollingRunning).forEach(tipo => {
      const tipoKey = tipo as 'tarefas' | 'avisos' | 'atividades' | 'estatisticas' | 'usuario';
      this.isPollingRunning[tipoKey] = false;
    });
    

  }
}

// Exportar uma instância única do serviço
export default new PollingService();
